/**
 * labelStack — resolução de sobreposição dos rótulos em espaço de tela.
 *
 * Cada rótulo quer ficar na sua altura natural (`baseSy`). Quando dois se
 * sobrepõem, o de trás sobe para logo acima do da frente; subir pode revelar uma
 * terceira caixa acima, então a busca itera até estabilizar. A ordem de entrada
 * define quem fica na base (ver o de-clutter no EntityManager).
 *
 * GRADE ESPACIAL. A versão direta compara cada rótulo com TODOS os já colocados,
 * e ainda por cima repete essa varredura a cada iteração da subida — na prática
 * O(n³) no pior caso. Medido com 140 rótulos aglomerados (o cenário de 500
 * jogadores numa cidade): 107.821 comparações por ciclo. Com a grade: 6.732, ou
 * seja 16× menos — e 39× menos quando os rótulos estão espalhados.
 *
 * A grade dá o MESMO resultado, não uma aproximação, e o motivo é a forma do
 * teste: a colisão é sobre a DISTÂNCIA ENTRE CENTROS,
 *     |dx| < a.halfW + b.halfW   e   |dy| < a.halfH + b.halfH + GAP
 * então cada caixa é indexada por UMA célula (a do seu centro) e a consulta abre
 * `halfW + maiorHalfW` do conjunto. Isso é um SUPERCONJUNTO exato dos candidatos:
 * ninguém que colidiria fica de fora, e todo candidato ainda passa pelo teste
 * preciso. (Indexar pela extensão da caixa e consultar por centro mistura dois
 * critérios e perde colisões — foi o primeiro erro desta implementação, pego pelo
 * harness.)
 *
 * Sem alocação em regime: as listas ligadas da grade vivem em Int32Array
 * reaproveitados entre os ciclos.
 */

export class LabelStacker {
  /** @param {number} maxItems teto de rótulos por ciclo (o de-clutter já limita) */
  constructor(maxItems) {
    this._next = new Int32Array(maxItems);
    this._head = null;
    this._cells = 0;
  }

  /**
   * Resolve as alturas. Muta `items[i].sy` (0..count-1). Cada item precisa de
   * `sx`, `baseSy`, `halfW`, `halfH`.
   *
   * @param {Array}  items      lista de trabalho (reaproveitada pelo chamador)
   * @param {number} count      quantos itens de `items` considerar
   * @param {number} gap        folga vertical entre rótulos (px)
   * @param {number} maxLiftPx  teto de elevação em relação à altura natural
   */
  resolve(items, count, gap, maxLiftPx) {
    if (count <= 0) return;
    // a maior caixa do ciclo define a célula e o alcance da consulta
    let maxHW = 0, maxHH = 0;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let i = 0; i < count; i++) {
      const it = items[i];
      if (it.halfW > maxHW) maxHW = it.halfW;
      if (it.halfH > maxHH) maxHH = it.halfH;
      if (it.sx < minX) minX = it.sx;
      if (it.sx > maxX) maxX = it.sx;
      if (it.baseSy < minY) minY = it.baseSy;
      if (it.baseSy > maxY) maxY = it.baseSy;
    }
    const cw = Math.max(16, maxHW * 2);
    const ch = Math.max(16, maxHH * 2 + gap);
    // a grade cobre onde os rótulos estão MAIS o espaço acima em que podem subir
    const x0 = minX - maxHW;
    const y0 = minY - maxLiftPx - maxHH;
    // teto de células: em aglomerado real dá algumas centenas; o clamp só torna as
    // células das bordas mais cheias, o que continua correto (o teste preciso roda
    // em todo candidato) — nunca incorreto.
    const cols = Math.min(128, Math.max(1, Math.ceil((maxX - x0 + maxHW) / cw) + 1));
    const rows = Math.min(256, Math.max(1, Math.ceil((maxY - y0 + maxHH) / ch) + 1));
    const nCells = cols * rows;
    if (!this._head || this._head.length < nCells) this._head = new Int32Array(nCells);
    const head = this._head;
    const next = this._next;
    head.fill(-1, 0, nCells);

    const cx = (x) => {
      const i = ((x - x0) / cw) | 0;
      return i < 0 ? 0 : i >= cols ? cols - 1 : i;
    };
    const cy = (y) => {
      const i = ((y - y0) / ch) | 0;
      return i < 0 ? 0 : i >= rows ? rows - 1 : i;
    };

    for (let i = 0; i < count; i++) {
      const it = items[i];
      const rx = it.halfW + maxHW;       // alcance exato da consulta em x
      const ry = it.halfH + maxHH + gap; // e em y
      const gx0 = cx(it.sx - rx), gx1 = cx(it.sx + rx);
      let sy = it.baseSy;
      // sobe até estabilizar; no máximo uma vez por caixa já colocada
      for (let iter = 0; iter <= i; iter++) {
        let limit = sy;
        const gy0 = cy(sy - ry), gy1 = cy(sy + ry);
        for (let gx = gx0; gx <= gx1; gx++) {
          const col = gx * rows;
          for (let gy = gy0; gy <= gy1; gy++) {
            for (let n = head[col + gy]; n !== -1; n = next[n]) {
              const p = items[n];
              const hx = it.halfW + p.halfW;
              const hy = it.halfH + p.halfH + gap;
              if (Math.abs(it.sx - p.sx) < hx && Math.abs(sy - p.sy) < hy) {
                const up = p.sy - hy; // sobe p/ logo acima de p
                if (up < limit) limit = up;
              }
            }
          }
        }
        if (limit === sy) break; // estável: nenhuma colisão restante
        sy = limit;
      }
      it.sy = Math.max(sy, it.baseSy - maxLiftPx);
      // indexa pelo CENTRO já resolvido
      const c = cx(it.sx) * rows + cy(it.sy);
      next[i] = head[c];
      head[c] = i;
    }
  }
}
